import { faTrash, faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Typography } from "@mui/material"
import { translate } from "app/language/service"
import { getRegionalizedLocale } from "app/language/tools/common"
import { IRootState } from "app/model"
import { DATE_FNS_LOCALE_BY_LOCALE } from "app/util/date/model"
import format from "date-fns/format"
import getDay from "date-fns/getDay"
import max from "date-fns/max"
import min from "date-fns/min"
import parse from "date-fns/parse"
import startOfWeek from "date-fns/startOfWeek"
import { IFormConfigurationSchedule } from "form/model"
import { getItemsByDay, getScheduleHoursText } from "form/tools/schedule"
import { Field, FieldProps, FormikErrors } from "formik"
import React from "react"
import { Calendar, Views, dateFnsLocalizer } from "react-big-calendar"
import { connect } from "react-redux"
import { ICampaignScheduleFormItem, IItemByDay } from "schedule/model"
import { FlexBox } from "ui/components/FlexBox"
import { FieldCheckbox } from "./FieldCheckbox"

// react-big-calendar => https://jquense.github.io/react-big-calendar/examples/index.html

interface IFieldWeekSchedulerProps {
  id: string
  items: ICampaignScheduleFormItem[]
  noSchedule: boolean
  scheduleErrors?: FormikErrors<IFormConfigurationSchedule>
  disabled?: boolean
  sx?: any
}

interface IFieldWeekSchedulerConnectProps {
  contentWidth: number
}

const FIELD_SCHEDULE_NAME: string = "schedule.scheduleItems"
const FIELD_RUN_ALL_TIME_NAME: string = "schedule.noSchedule"

const FieldWeekSchedulerFn = React.memo(
  ({
    id,
    disabled,
    items,
    noSchedule,
    scheduleErrors,
    sx,
    contentWidth
  }: IFieldWeekSchedulerProps & IFieldWeekSchedulerConnectProps) => {
    const groupedByDayItems: IItemByDay[] = getItemsByDay(items)
    const setItems = (
      setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
      newItems: ICampaignScheduleFormItem[]
    ) => {
      newItems.sort((i1, i2) => (i1.start < i2.start ? -1 : 1))
      setFieldValue(FIELD_SCHEDULE_NAME, newItems)
    }
    return (
      <FlexBox id={id} vertical ai="start" fullWidth className={disabled ? "Mui-disabled" : undefined}>
        {scheduleErrors?.scheduleItems && (
          <Typography id={`${id}-error`} color="error" fontSize=".8rem">
            {scheduleErrors.scheduleItems}
          </Typography>
        )}
        <FieldCheckbox
          fieldName={FIELD_RUN_ALL_TIME_NAME}
          label={translate("label.ad.run.all.time")}
          value={noSchedule}
          disabled={disabled}
        />
        <Field name={FIELD_SCHEDULE_NAME}>
          {({ form: { setFieldValue } }: FieldProps) => (
            <FlexBox
              ai="start"
              gap
              fullWidth
              sx={{
                "pointerEvents": disabled || noSchedule ? "none" : undefined,
                "opacity": disabled || noSchedule ? 0.4 : 1,
                "transition": "opacity 400ms ease",
                "& .rbc-toolbar, & .rbc-allday-cell": { display: "none" },
                "& button.rbc-button-link": {
                  overflow: "hidden",
                  pointerEvents: "none"
                },
                "& button.rbc-button-link > span": {
                  marginLeft: "-1rem",
                  pointerEvents: "none"
                },
                "& .rbc-event-label": {
                  whiteSpace: "pre-wrap"
                },
                "& .rbc-time-slot": {
                  "display": "flex",
                  "justifyContent": "center",
                  "alignItems": "center",
                  "& .rbc-label": {
                    fontSize: ".9rem"
                  }
                },
                "& .rbc-timeslot-group": {
                  minHeight: "1.5rem",
                  maxHeight: "1.5rem"
                },
                "& .rbc-event": {
                  backgroundColor: theme => theme.palette.primary.main,
                  color: theme => theme.palette.formText,
                  border: theme => theme.palette.border,
                  visibility: disabled || noSchedule ? "hidden" : undefined,
                  transition: "visibility 200ms ease"
                },
                "@media (max-width: 1200px)": {
                  "flexWrap": "wrap",
                  "& .week-summary": {
                    width: "100%"
                  }
                },
                "& .week-scheduler": {
                  width: "100%"
                },
                "& .rbc-time-column.rbc-today, & .rbc-header.rbc-today": {
                  backgroundColor: "transparent"
                },
                "& .rbc-current-time-indicator": {
                  visibility: "hidden"
                },
                "& .rbc-header": {
                  borderBottom: "none"
                },
                "& .rbc-time-content": {
                  borderWidth: ".05rem"
                },
                "& .rbc-time-view": {
                  borderBottom: "none"
                },
                ...sx
              }}>
              <Calendar
                className="week-scheduler"
                // !! CULTURE = LOCALE !!
                culture={getRegionalizedLocale()}
                dayLayoutAlgorithm="no-overlap"
                defaultDate={new Date()}
                defaultView={Views.WEEK}
                events={items}
                localizer={dateFnsLocalizer({
                  format,
                  parse,
                  startOfWeek,
                  getDay,
                  locales: { ...DATE_FNS_LOCALE_BY_LOCALE }
                })}
                onSelectEvent={item =>
                  setItems(
                    setFieldValue,
                    items.filter(i => i !== item)
                  )
                }
                onSelectSlot={({ start, end }) => {
                  const intersectedEvents = items.filter(i => {
                    if (i.start?.getDate() !== start.getDate()) {
                      return false
                    }
                    if (i.start <= start) {
                      return (i.end as Date) >= start
                    }
                    return i.start <= end
                  })
                  if (intersectedEvents.length === 0) {
                    setItems(setFieldValue, [...items, { start, end }])
                  } else {
                    setItems(setFieldValue, [
                      ...items.filter(e => !intersectedEvents.includes(e)),
                      {
                        start: min([...intersectedEvents.map(e => e.start as Date), start]),
                        end: max([...intersectedEvents.map(e => e.end as Date), end])
                      }
                    ])
                  }
                }}
                timeslots={1}
                step={60}
                selectable
              />
              {!disabled && (
                <FlexBox
                  vertical
                  ai="start"
                  sx={{
                    "ml": "1rem",
                    "minWidth": contentWidth > 660 ? "22rem" : undefined,
                    "& > *": {
                      borderBottom: theme => theme.palette.border
                    }
                  }}>
                  <Typography
                    color="textPrimary"
                    sx={{ borderBottom: "none !important", fontWeight: 500, marginBottom: "1rem" }}>
                    {translate("label.schedule.summary")}
                  </Typography>
                  {noSchedule ? (
                    <Typography
                      color="textPrimary"
                      sx={{ mt: "1rem", borderBottom: "none !important", fontWeight: 500 }}>
                      {translate("label.no.schedule")}
                    </Typography>
                  ) : groupedByDayItems.length === 0 ? (
                    <Typography color="textSecondary" sx={{ mt: "1rem", borderBottom: "none !important" }}>
                      {translate("label.schedule.summary.empty")}
                    </Typography>
                  ) : (
                    groupedByDayItems.map(group => (
                      <FlexBox
                        className="week-summary"
                        key={group.day}
                        sx={{
                          border: theme => theme.palette.border
                        }}>
                        <Typography color="textPrimary" sx={{ minWidth: "8rem", p: ".5rem", pl: "1rem", pr: "1rem" }}>
                          {translate(`label.day.${group.day}`)}
                        </Typography>
                        <FlexBox
                          vertical
                          sx={{
                            minWidth: "13rem",
                            p: ".5rem",
                            pl: "1rem",
                            pr: "1rem",
                            borderLeft: theme => theme.palette.border
                          }}>
                          {group.items.map((item, index) => (
                            <FlexBox
                              key={index}
                              jc="space-between"
                              fullWidth
                              sx={{
                                "& .remove-icon": {
                                  ml: "1rem",
                                  color: theme => theme.palette.text.secondary,
                                  cursor: "pointer"
                                },
                                "&:hover *": {
                                  color: theme => theme.palette.text.primary
                                }
                              }}>
                              <Typography fontFamily="arial" fontSize=".9rem" color="textSecondary">
                                {getScheduleHoursText(item.start, item.end)}
                              </Typography>
                              <FontAwesomeIcon
                                icon={faXmark}
                                className="remove-icon"
                                onClick={() =>
                                  setItems(
                                    setFieldValue,
                                    items.filter(i => i !== item)
                                  )
                                }
                              />
                            </FlexBox>
                          ))}
                        </FlexBox>
                      </FlexBox>
                    ))
                  )}
                  {groupedByDayItems.length > 0 && !noSchedule && (
                    <FlexBox
                      onClick={() => setItems(setFieldValue, [])}
                      jc="end"
                      fullWidth
                      sx={{
                        "mt": "1rem",
                        "border": "none",
                        "cursor": "pointer",
                        "& > *": { color: theme => theme.palette.text.secondary, transition: "color 400ms ease" },
                        "&:hover > *": { color: theme => theme.palette.text.primary }
                      }}>
                      <FontAwesomeIcon icon={faTrash} />
                      <Typography sx={{ ml: ".4rem", fontSize: ".9rem", borderBottom: "none", fontWeight: 500 }}>
                        {translate("label.clear.all")}
                      </Typography>
                    </FlexBox>
                  )}
                </FlexBox>
              )}
            </FlexBox>
          )}
        </Field>
      </FlexBox>
    )
  }
)

export const FieldWeekScheduler = connect(
  (state: IRootState): IFieldWeekSchedulerConnectProps => ({
    contentWidth: state.ui.contentWidth
  })
)(FieldWeekSchedulerFn)
